/**
 * The submitter's answers beyond title and problem (#1628, #1629), shown where
 * a demand is worked: the demand page (questions 3, 6 and the files — the rest
 * already has a home there) and the Sent to DSAI page (everything). Renders
 * nothing when there is nothing to show — the server leaves these empty for a
 * viewer who may not read them.
 */

import { Button, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowDownload16Regular } from '@fluentui/react-icons'
import { downloadDemandAttachment } from '../hooks/useDemandAttachments'
import { useToast } from './ToastProvider'
import type { DemandAttachment, RelatedWork } from '../types/demand'

const RELATED_WORK_LABEL: Record<RelatedWork, string> = {
  no: 'No',
  yes: 'Yes',
  not_sure: 'Not sure',
}

const useStyles = makeStyles({
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 180px) 1fr',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
  },
  key: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
  value: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  files: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
})

export interface RequestAnswersProps {
  targetUser?: string | null
  benefit?: string | null
  background?: string | null
  relatedWork?: RelatedWork | string | null
  relatedWorkDetails?: string | null
  importantDate?: string | null
  importantDateReason?: string | null
  attachments?: DemandAttachment[]
}

/** "2026-12-01" → "1 Dec 2026", read as a calendar date (no time zone shift). */
export function formatCalendarDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function hasRequestAnswers(p: RequestAnswersProps): boolean {
  return !!(
    p.targetUser ||
    p.benefit ||
    p.background ||
    p.relatedWork ||
    p.importantDate ||
    p.importantDateReason ||
    p.attachments?.length
  )
}

export function RequestAnswers(props: RequestAnswersProps) {
  const styles = useStyles()
  const toast = useToast()
  const {
    targetUser,
    benefit,
    background,
    relatedWork,
    relatedWorkDetails,
    importantDate,
    importantDateReason,
    attachments,
  } = props
  if (!hasRequestAnswers(props)) return null

  const related = relatedWork
    ? (RELATED_WORK_LABEL[relatedWork as RelatedWork] ?? relatedWork)
    : null

  return (
    <div data-testid="request-answers">
      {targetUser && (
        <div className={styles.row}>
          <span className={styles.key}>Who would benefit</span>
          <div className={styles.value}>{targetUser}</div>
        </div>
      )}
      {benefit && (
        <div className={styles.row}>
          <span className={styles.key}>Expected benefit</span>
          <div className={styles.value}>{benefit}</div>
        </div>
      )}
      {related && (
        <div className={styles.row}>
          <span className={styles.key}>Related work</span>
          <div className={styles.value}>
            {related}
            {relatedWorkDetails ? ` — ${relatedWorkDetails}` : ''}
          </div>
        </div>
      )}
      {(importantDate || importantDateReason) && (
        <div className={styles.row}>
          <span className={styles.key}>Important date</span>
          <div className={styles.value}>
            {importantDate ? formatCalendarDate(importantDate) : ''}
            {importantDate && importantDateReason ? ' — ' : ''}
            {importantDateReason ?? ''}
          </div>
        </div>
      )}
      {background && (
        <div className={styles.row}>
          <span className={styles.key}>Anything else</span>
          <div className={styles.value}>{background}</div>
        </div>
      )}
      {!!attachments?.length && (
        <div className={styles.row}>
          <span className={styles.key}>Files</span>
          <div className={styles.files}>
            {attachments.map((f) => (
              <Button
                key={f.id}
                appearance="transparent"
                size="small"
                icon={<ArrowDownload16Regular aria-hidden />}
                onClick={() =>
                  void downloadDemandAttachment(f).then((reason) => {
                    if (reason) toast.showToast({ title: reason, intent: 'error' })
                  })
                }
                data-testid={`request-file-${f.id}`}
              >
                {f.filename}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
